// Reconnecting a voice conversation that ended under the user (#2579): every backend deploy restarts the app and
// drops every socket, and a provider or network hiccup ends a session the same way.

/**
 * The ends that say nothing about the user, their chat or their allowance, so a new session may well work: the
 * connection closed without an end event (`disconnected`, which is what a deploy's restart looks like), the provider
 * failed, the server could not confirm its own checks in time (`unavailable`), the client fell behind
 * (`slow_client`), and a hub that did not come up or closed before it said anything (`connect_failed`), which only
 * reaches this during a reconnect.
 *
 * Everything else is a decision or a limit and stays final: the user stopped, a newer session took over
 * (`superseded`), the user started a newer conversation while this one waited (`reconnect_superseded`), a typed
 * question took the chat, a refusal (`voice_unavailable`, `flag_off`, `revoked`, `expired`, `microphone`), a browser
 * that keeps a reconnect's audio suspended until the user clicks (`audio_blocked`), a limit (`limit`, `max_length`,
 * `idle`, `rate_limited`), or the chat changed hands.
 */
const TRANSIENT_ENDS: ReadonlySet<string> = new Set([
    'disconnected',
    'provider_error',
    'unavailable',
    'slow_client',
    'connect_failed',
]);

export const isTransientEnd = (reason: string): boolean => TRANSIENT_ENDS.has(reason);

/** At most this many new sessions per {@link RECONNECT_WINDOW_MS}, however each of them ends. */
export const MAX_RECONNECTS = 3;
export const RECONNECT_WINDOW_MS = 60_000;

/**
 * At most this many in one conversation. The window alone bounds how often, not how long: sessions that come up and
 * drop soon after would reconnect three times a minute for as long as voice mode stays on.
 */
export const MAX_RECONNECTS_IN_ALL = 10;

/**
 * How much later than planned an attempt may come due and still reconnect. A browser holds back the timers of a tab
 * in the background, Chrome by up to a minute, and the user may be talking from that tab. An attempt later than that
 * comes from a laptop that slept or a tab that was frozen: the server may have forgotten by then the newer
 * conversation it would refuse the reconnect for (VoiceSessionRegistry.ConversationMemory, ten minutes), and the
 * microphone would open long after the user stopped talking.
 */
export const MAX_RECONNECT_LATENESS_MS = 60_000;

// The nominal wait before the first, second and third attempt in a row. Together they cover the better part of a
// minute, about what the app takes to come back after a deploy.
const RECONNECT_DELAYS_MS = [2_000, 10_000, 30_000];

/** Decides, end by end, whether a conversation reconnects and after how long. One per conversation the user started. */
export class ReconnectPolicy {
    private readonly attempts: number[] = [];
    private failedInARow = 0;
    private total = 0;

    constructor(private readonly random: () => number = Math.random) {}

    /** The wait before the next attempt; undefined when this end is final or the attempts are spent. */
    nextDelay(reason: string, now: number): number | undefined {
        if (!isTransientEnd(reason)) {
            return undefined;
        }
        while (this.attempts.length > 0 && now - this.attempts[0] >= RECONNECT_WINDOW_MS) {
            this.attempts.shift();
        }
        // Counted across sessions that came up in between, too: a server that fails every session soon after it
        // starts would otherwise be reconnected to, and billed for, without end.
        if (this.attempts.length >= MAX_RECONNECTS) {
            return undefined;
        }
        // Each wait once: attempts that each take long to fail would otherwise go on for good, since the oldest one
        // leaves the window before the third has failed.
        if (this.failedInARow >= RECONNECT_DELAYS_MS.length || this.total >= MAX_RECONNECTS_IN_ALL) {
            return undefined;
        }
        this.attempts.push(now);
        this.total++;
        const nominal = RECONNECT_DELAYS_MS[this.failedInARow];
        this.failedInARow++;
        // Drawn from the upper half, so the sessions one deploy dropped together do not all come back in the same
        // second.
        return nominal / 2 + this.random() * (nominal / 2);
    }

    /** A session came up again: a later drop starts over from the shortest wait, though it still counts. */
    onReady(): void {
        this.failedInARow = 0;
    }
}
